import { DIGITAL_ADS } from '../config';
import { toDateInputValue } from './pricing';

// Calls the availability Netlify Function for a set of Wednesday dates.
// Returns { ok: true, counts } on success, or { ok: false } on any failure —
// callers treat { ok: false } as "availability unknown" and fail open (allow
// the booking) so a backend hiccup never blocks checkout.
export async function fetchAvailability(dates) {
  const ymd = dates.map((d) => (d instanceof Date ? toDateInputValue(d) : d));
  try {
    const res = await fetch('/.netlify/functions/availability', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ dates: ymd }),
    });
    const data = await res.json();
    return data && data.ok ? data : { ok: false };
  } catch (err) {
    console.warn('Availability check unavailable — allowing booking:', err);
    return { ok: false };
  }
}

// Given availability counts and a run schedule, returns a map of
// { [digitalAdId]: { soldOut, fullDates } }. A web/email slot is sold out for
// this booking if it is already full on ANY of the requested weeks (the ad runs
// every week, so it must be open every week). A week on or before an ad's
// `bookedThrough` date is always full. Otherwise, if availability is unknown,
// nothing is marked sold out.
// `skips` is { [adId]: ['YYYY-MM-DD', …] } — weeks the advertiser has chosen to
// skip for a placement. A skipped week is never a conflict (the ad isn't running
// that Wednesday), so it's excluded from the sold-out check.
export function getDigitalAvailability(availability, runScheduleDates, skips = {}) {
  const result = {};
  const known  = !!(availability && availability.ok);
  const counts = (known && availability.counts) || {};
  const dates = (runScheduleDates || []).map((d) => (d instanceof Date ? toDateInputValue(d) : d));

  for (const ad of DIGITAL_ADS) {
    const tracked = known && !!ad.capacity; // capacity null = unlimited / not tracked
    if (!tracked && !ad.bookedThrough) continue;
    const adSkips = skips[ad.id] || [];
    const fullDates = dates.filter((date) => {
      if (adSkips.includes(date)) return false; // already skipping this week
      if (isBookedThrough(ad, date)) return true;
      if (!tracked) return false;
      const booked = (counts[date] && counts[date][ad.manifestLabel]) || 0;
      return booked >= ad.capacity;
    });
    result[ad.id] = { soldOut: fullDates.length > 0, fullDates };
  }
  return result;
}

// True when `date` ('YYYY-MM-DD') falls on or before the ad's manual
// `bookedThrough` date. ISO date strings compare correctly as plain strings.
export function isBookedThrough(ad, date) {
  return !!(ad && ad.bookedThrough && date <= ad.bookedThrough);
}

// The first Wednesday (from `wednesdays`, in order) where a run of `weeks`
// weeks starting that day has `ad` open every week — the "Next opening" the
// Digital step offers on a sold-out placement. Prefers a start on or after
// `fromYmd` (the advertiser's current start); falls back to the earliest.
// Returns a Date from `wednesdays`, or null if no start in the list works.
export function findNextOpenStart(ad, availability, wednesdays, weeks, fromYmd) {
  const isOpen = (wed) => {
    const schedule = [];
    const d = new Date(wed);
    for (let i = 0; i < weeks; i++) {
      schedule.push(toDateInputValue(d));
      d.setDate(d.getDate() + 7);
    }
    const info = getDigitalAvailability(availability, schedule)[ad.id];
    return !(info && info.soldOut);
  };
  const later = wednesdays.filter((w) => !fromYmd || toDateInputValue(w) >= fromYmd);
  return later.find(isOpen) || wednesdays.find(isOpen) || null;
}
